/**
 * Front-end behavior for the mea/case-studies tab strip: an ARIA tablist
 * (manual activation) that switches which category tab is "active" -- moving
 * the teal pill + white label + pulsing dot to the clicked/activated tab, and
 * swapping which panel carries `is-active`. Every tab's headline, body and
 * outcome list is a block attribute, so render.php prints one panel per tab
 * and this file only moves the active state between them; the tab count is
 * whatever the page saved, never a fixed 5.
 *
 * Markup rendered by render.php:
 *
 *   <div class="mea-case-studies__tabs" role="tablist">
 *     <button class="mea-case-studies__tab [--active]" role="tab"
 *             id="{tab-id}" aria-selected="true/false"
 *             aria-controls="{panel-id}" tabindex="0/-1">
 *       <span class="mea-case-studies__tab-dot [mea-pulse]" aria-hidden="true"></span>
 *       <span class="mea-case-studies__tab-label">...</span>
 *     </button>
 *     ...
 *   </div>
 *   <div class="mea-case-studies__content [is-active]" id="{panel-id}"
 *        role="tabpanel" aria-labelledby="{tab-id}">
 *     ...
 *   </div>
 *
 * Manual activation model (per WAI-ARIA APG "Tabs"): Left/Right and Home/End
 * arrow keys move a roving-tabindex focus between tabs WITHOUT selecting
 * them; a click, or Enter/Space on a focused tab (native <button> behavior --
 * no extra key handling needed), selects it and moves the active styling +
 * pulse dot there. Only one tab is ever active at a time.
 *
 * Deck slide 03 also adds three motion pieces on top of the above, all pure
 * CSS transitions (no GSAP dependency here, same split as mea/faq: the
 * scroll entrance lives in _shared/animation.js, interaction lives here):
 *
 *   - the sliding pill (`.mea-case-studies__tab-pill`, styled in style.scss)
 *     is repositioned to the active tab's own measured rect on init and on
 *     every switch; the CSS transition on transform/width/height is what
 *     actually animates the slide.
 *   - the panel crossfade (opacity/transform/visibility) is CSS-only,
 *     already driven by the existing `.is-active` toggle below.
 *   - bullet items stagger in per activated panel: each gets a
 *     transition-delay of its index * 100ms right before `.is-active` lands,
 *     matching "100ms delay between items" without a fixed-length list.
 */
( function () {
	const CARD_ITEM_STAGGER_MS = 100;

	function positionPill( pill, tab, container, animate ) {
		if ( ! pill || ! tab ) {
			return;
		}
		const containerRect = container.getBoundingClientRect();
		const tabRect = tab.getBoundingClientRect();

		if ( ! animate ) {
			pill.style.transition = 'none';
		}
		// translateY matters once the tab strip wraps onto a second row -
		// without it, an active tab on row 2 leaves the pill stranded on
		// row 1's y position.
		pill.style.transform =
			'translate(' +
			( tabRect.left - containerRect.left ) +
			'px, ' +
			( tabRect.top - containerRect.top ) +
			'px)';
		pill.style.width = tabRect.width + 'px';
		pill.style.height = tabRect.height + 'px';
		if ( ! animate ) {
			// Force layout so the suppressed transition above is committed
			// before it is re-enabled, or the very next style change (e.g. a
			// resize immediately after load) would slide in from zero.
			void pill.offsetWidth;
			pill.style.transition = '';
		}
	}

	function staggerCardItems( panel ) {
		if ( ! panel ) {
			return;
		}
		panel
			.querySelectorAll( '.mea-case-studies__card-item' )
			.forEach( function ( item, i ) {
				item.style.transitionDelay =
					i * CARD_ITEM_STAGGER_MS + 'ms';
			} );
	}

	function initCaseStudies( root ) {
		const tabs = Array.prototype.slice.call(
			root.querySelectorAll( '.mea-case-studies__tab' )
		);
		if ( ! tabs.length ) {
			return;
		}

		const panels = Array.prototype.slice.call(
			root.querySelectorAll( '.mea-case-studies__content' )
		);
		const tabsContainer = root.querySelector(
			'.mea-case-studies__tabs'
		);
		const pill = root.querySelector( '.mea-case-studies__tab-pill' );

		function selectedIndex() {
			const index = tabs.findIndex( function ( tab ) {
				return tab.getAttribute( 'aria-selected' ) === 'true';
			} );
			return index >= 0 ? index : 0;
		}

		// Roving tabindex follows keyboard focus (not selection) so that
		// Tab/Shift+Tab always re-enters the tablist at whichever tab was
		// last visited by arrow keys, matching the manual-activation pattern.
		function setRovingTabindex( index ) {
			tabs.forEach( function ( tab, i ) {
				tab.tabIndex = i === index ? 0 : -1;
			} );
		}

		function focusTabAt( index ) {
			setRovingTabindex( index );
			tabs[ index ].focus();
		}

		function selectTab( index ) {
			if ( index === selectedIndex() ) {
				setRovingTabindex( index );
				return;
			}

			tabs.forEach( function ( tab, i ) {
				const isActive = i === index;
				tab.classList.toggle(
					'mea-case-studies__tab--active',
					isActive
				);
				tab.setAttribute(
					'aria-selected',
					isActive ? 'true' : 'false'
				);

				const dot = tab.querySelector( '.mea-case-studies__tab-dot' );
				if ( dot ) {
					dot.classList.toggle( 'mea-pulse', isActive );
				}
			} );

			setRovingTabindex( index );

			positionPill( pill, tabs[ index ], tabsContainer, true );
			staggerCardItems( panels[ index ] );

			panels.forEach( function ( panel, i ) {
				panel.classList.toggle( 'is-active', i === index );
			} );
		}

		tabs.forEach( function ( tab, i ) {
			tab.addEventListener( 'click', function () {
				selectTab( i );
			} );

			tab.addEventListener( 'keydown', function ( event ) {
				const count = tabs.length;

				switch ( event.key ) {
					case 'ArrowRight':
						event.preventDefault();
						focusTabAt( ( i + 1 ) % count );
						break;
					case 'ArrowLeft':
						event.preventDefault();
						focusTabAt( ( i - 1 + count ) % count );
						break;
					case 'Home':
						event.preventDefault();
						focusTabAt( 0 );
						break;
					case 'End':
						event.preventDefault();
						focusTabAt( count - 1 );
						break;
					default:
						break;
				}
			} );
		} );

		// Initial placement, unanimated - the button rects are only known
		// once laid out, and the deck's slide is a click-driven interaction,
		// not something that should also play on first paint.
		positionPill( pill, tabs[ selectedIndex() ], tabsContainer, false );

		// Button widths change across breakpoints (padding, gap, wrapping),
		// so the pill's measured rect goes stale on resize. Debounced with
		// rAF rather than run per-event, same reasoning as ScrollTrigger's
		// own refresh being deferred to settle points elsewhere in the
		// motion layer.
		let resizeFrame = null;
		window.addEventListener( 'resize', function () {
			if ( resizeFrame ) {
				return;
			}
			resizeFrame = window.requestAnimationFrame( function () {
				resizeFrame = null;
				positionPill(
					pill,
					tabs[ selectedIndex() ],
					tabsContainer,
					false
				);
			} );
		} );
	}

	function boot() {
		document
			.querySelectorAll( '.mea-case-studies' )
			.forEach( initCaseStudies );
	}

	if ( document.readyState !== 'loading' ) {
		boot();
	} else {
		document.addEventListener( 'DOMContentLoaded', boot );
	}
} )();
